import { Avatar, Badge, Chip, Divider, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import clsx from 'clsx';
import { ErrorMessage, Field } from 'formik';
import { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { BiVideo } from 'react-icons/bi';
import { FiPhone, FiUpload } from 'react-icons/fi';
import { HiOutlineMail } from 'react-icons/hi';
import { IoCloseCircleSharp } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import { SelectionAutocomplete } from 'components/Autocomplete/SelectionAutocomplete';
import { FormSelectField, FormTextField } from 'components/TextField';
import { getContentById, getContentVersions } from 'features/content/contentSlice';
import {
    getProceduresPicklist,
    selectProceduresPicklist,
    selectProceduresPicklistLoadingStatus,
} from 'features/picklists/pickListsSlice';
import { getProcedureById, getProcedureOriginalData } from 'features/procedures/proceduresSlice';
import { ProcedureStartEndNodeField } from 'forms/ContentVersionForm/components/ProcedureStartEndNodeField';
import { panelStyles } from 'GeminiViewerComponent/components/styles';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { mapVideoContentURL } from 'GeminiViewerComponent/helpers';
import { LinkTypes } from 'helpers';
import { makeFormStyles } from '../../styles';
import { ConfigHeader } from './ConfigSourceHeader';
import { ContentChapterFields } from './ContentChapterFields';
import { ImageConfigs } from './ImageConfigs';
import { VideoConfigs } from './VideoConfigs';

export const ContentFormFields = ({
    contentId,
    editMode,
    createMode,
    readOnlyMode,
    values,
    getContentTypes,
    getDefaultContentTypeId,
    initValues,
    setInitValues,
    handleToggle,
    getContentMaxSize,
    onDrop,
    onDropError,
    validateVideoCompression,
    handleViewLink,
    handleCreateContentClick,
    setFieldError,
    setFieldTouched,
    setFieldValue,
    imageSettings,
    setImageSettings,
    setIsVideoCompressionEnable,
    configValues,
    configFields,
    validationTypes,
    // Key that when incremented remounts the Dropzone, clearing its internal state.
    // Passed by ContentForm when the user toggles between URL and File mode for Learning items.
    dropzoneKey = 0,
}) => {
    const dispatch = useDispatch();
    const theme = useSelector(selectDefaultTheme);
    const classes = makeFormStyles(theme);
    const panelClasses = panelStyles(theme);
    const [defaultConfigValues, setDefaultConfigValues] = useState({});
    const contentVersions = useSelector(getContentVersions);
    const sortedContentVersion = [...contentVersions].sort((a, b) => a.version - b.version);
    const procedureOriginalData = useSelector(getProcedureOriginalData);
    const content = useSelector((state) => getContentById(state, contentId));
    const procedureNodes =
        procedureOriginalData?.nodes?.map((node) => {
            return { id: node.id, title: node?.title };
        }) || [];

    const [procedureStartEndNode, setProcedureStartEndNode] = useState({
        procedureStartNode: procedureNodes?.find((node) => node?.id === values?.procedure_start_node) || null,
        procedureEndNode: procedureNodes?.find((node) => node?.id === values?.procedure_end_node) || null,
    });

    useEffect(() => {
        if (Array.isArray(configValues)) {
            let defaultConfig = {};
            var imageAndVideoConfigKeys = [
                'convert_image_to_jpeg',
                'compress_videos',
                'enable_hls',
                'image_compression_percent',
                'max_image_width',
                'max_video_height',
                'transcribe_videos',
                'transcribe_language',
            ];

            let audioVideoConfigFields = configFields?.filter((fld) => imageAndVideoConfigKeys.includes(fld.key));

            audioVideoConfigFields.map((fld) => {
                defaultConfig = {
                    ...defaultConfig,
                    [fld.key]: configValues.find((data) => data.config_field_id == fld.config_field_id),
                };
            });

            setDefaultConfigValues(defaultConfig);
        }
    }, [configValues, configFields]);

    useEffect(() => {
        setFieldValue('compress_videos', null);
        setFieldValue('enable_hls', null);
        setFieldValue('max_video_height', null);
        setFieldValue('convert_image_to_jpeg', null);
        setFieldValue('image_compression_percent', null);
        setFieldValue('max_image_width', null);
        setFieldValue('transcribe_videos', null);
        setFieldValue('transcribe_language', null);
        setFieldValue('defaultConfigValues', defaultConfigValues);

        if (Object.keys(defaultConfigValues).length > 0) {
            if (values.link_type_id === LinkTypes.Video.id) {
                setFieldValue('compress_videos', defaultConfigValues.compress_videos.value === 'true' ? true : false);
                setFieldValue('enable_hls', defaultConfigValues.enable_hls.value === 'true' ? true : false);
                setFieldValue('max_video_height', defaultConfigValues.max_video_height.value);
                setFieldValue(
                    'transcribe_videos',
                    defaultConfigValues?.transcribe_videos?.value === 'true' ? true : false
                );
                setFieldValue('transcribe_language', defaultConfigValues.transcribe_language.value);
            }
            if (values.link_type_id === LinkTypes.Image.id) {
                setFieldValue(
                    'convert_image_to_jpeg',
                    defaultConfigValues.convert_image_to_jpeg.value === 'true' ? true : false
                );
                setFieldValue('image_compression_percent', defaultConfigValues.image_compression_percent.value);
                setFieldValue('max_image_width', defaultConfigValues.max_image_width.value);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.link_type_id, defaultConfigValues]);

    useEffect(() => {
        (async () => {
            if (values?.procedure_id) {
                let procedureNodes = [];
                if (values?.procedure_id !== procedureOriginalData?.procedure_id) {
                    dispatch(activateLoading());
                    const resultAction = await dispatch(getProcedureById(values.procedure_id));

                    const procedure = JSON.parse(resultAction.payload?.procedure_json);
                    procedureNodes =
                        procedure?.nodes?.map((node) => {
                            return { id: node.id, title: node?.title };
                        }) || [];

                    dispatch(deactivateLoading());
                } else {
                    procedureNodes =
                        procedureOriginalData?.nodes?.map((node) => {
                            return { id: node.id, title: node?.title };
                        }) || [];
                }
                setProcedureStartEndNode({
                    procedureStartNode:
                        procedureNodes?.find((node) => node?.id === values?.procedure_start_node) || null,
                    procedureEndNode: procedureNodes?.find((node) => node?.id === values?.procedure_end_node) || null,
                });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.procedure_id, values?.procedure_start_node, values?.procedure_end_node]);

    const handleVersionChange = (e) => {
        let data = contentVersions.find((obj) => obj.content_version_id === +e.target.value);
        if (data) {
            let chapters = [];
            if (data?.chapters && data?.chapters.length > 0) {
                chapters = [...data.chapters];
                chapters?.sort((a, b) => (a?.chapter_id > b?.chapter_id ? 1 : -1));
            }
            setInitValues({
                ...initValues,
                toggleFormType: data?.embed_data ? '' : data?.original_file_name ? 'file' : 'url',
                url: data?.embed_data || data?.procedure_id || data?.original_file_name ? '' : data?.url,
                image_url: mapVideoContentURL(data),
                content_version_type: data?.procedure_id ? 4 : data?.embed_data ? 3 : data?.original_file_name ? 2 : 1,
                procedure_start_node: data?.procedure_start_node,
                procedure_end_node: data?.procedure_end_node,
                procedure_display_all_nodes: data?.procedure_display_all_nodes,
                procedure_force_start_node: data?.procedure_force_start_node,
                chapters: chapters,
                version: data.content_version_id,
                content_data: null,
                img_name: data?.original_file_name,
                embed_data: data?.embed_data,
                procedure_id: data?.procedure_id,
                overlay_text: (data?.overlay_text ? JSON.parse(data?.overlay_text) : '') ?? null,
                content_files: data?.content_files ?? [],
                launch_file: data?.launch_file?.replace(/\\/g, '/') ?? '',
            });
        }
    };

    const getConfigOptionsArray = (configFields, validationTypes, defaultConfigValues, key = null) => {
        if (
            Array.isArray(configFields) &&
            Array.isArray(validationTypes) &&
            Object.keys(defaultConfigValues ?? {})?.length > 0 &&
            key
        ) {
            let configFieldId = defaultConfigValues?.[key]?.config_field_id;
            if (configFieldId) {
                let validationTypeId = configFields.find(
                    (fld) => fld.config_field_id === configFieldId
                )?.validation_type_id;
                if (validationTypeId) {
                    let options = validationTypes.find(
                        (valid) => valid?.validation_type_id === validationTypeId
                    )?.options_array;
                    if (Array.isArray(options)) {
                        return options;
                    }
                }
            }
        }
        return [];
    };

    const getAcceptForLinkType = (linkTypeId) => {
        if (linkTypeId === LinkTypes.Learning.id) {
            return '.zip';
        }
        const linkType = Object.values(LinkTypes).find((lt) => lt.id === linkTypeId);
        const extensions = linkType?.contentTypes
            ?.map((ct) => ct.extension)
            .filter(Boolean);
        return extensions?.length ? extensions.join(',') : null;
    };

    const contentTypes = getContentTypes(values.link_type_id);
    const handleContentUrlChanged = (e) => {
        const value = e.target.value;
        setFieldValue('url', value);
        setFieldTouched('url', true, false);
        if (!value) return;

        if (values.link_type_id === LinkTypes.Learning.id) {
            const htmlType = contentTypes.find((ct) => ct.label === 'HTML');
            if (htmlType) {
                setFieldValue('content_type_id', htmlType.id);
            }
            return;
        }

        const ext = value.substring(value.lastIndexOf('.'));
        for (const ct of contentTypes) {
            if (ext.toLowerCase() === ct.extension) {
                setFieldValue('content_type_id', ct.id);
            }
        }
    };

    return (
        <>
            {editMode && values.link_type_id !== LinkTypes.SubjectMatterExpert.id && (
                <FormSelectField
                    name="version"
                    entities={sortedContentVersion}
                    onChange={handleVersionChange}
                    valueProperty="content_version_id"
                    nameProperty="version"
                    keyProperty="content_version_id"
                    namePrefix="Version "
                />
            )}
            {values.link_type_id !== LinkTypes.Procedure.id &&
                values.link_type_id !== LinkTypes.Embed.id &&
                values.link_type_id !== LinkTypes.SubjectMatterExpert.id && (
                    <>
                        <RadioGroup
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                minWidth: '150px',
                            }}
                            name="type"
                            value={values.toggleFormType}
                            onChange={(e) => handleToggle(e.target.value, setFieldValue, values.link_type_id, setFieldTouched, setFieldError)}
                            disabled={readOnlyMode}
                        >
                            <FormControlLabel
                                value="url"
                                control={<Radio color="primary" />}
                                label="URL"
                                disabled={readOnlyMode}
                            />
                            <FormControlLabel
                                value="file"
                                control={<Radio color="primary" />}
                                label="File"
                                disabled={readOnlyMode}
                            />
                        </RadioGroup>
                        {values.toggleFormType === 'url' && (
                            <>
                                <FormTextField
                                    label="Content URL"
                                    name="url"
                                    placeholder={`Content URL${createMode && values.link_type_id !== '' ? '*' : ''}`}
                                    disabled={readOnlyMode}
                                    onChange={handleContentUrlChanged}
                                />
                                {values.link_type_id !== LinkTypes.Learning.id && (
                                    <FormSelectField
                                        name="content_type_id"
                                        label="Select Url Type"
                                        entities={contentTypes}
                                        valueProperty="id"
                                        nameProperty="label"
                                        keyProperty="label"
                                        defaultOptionName="Select Url Type"
                                        defaultOptionValue
                                        defaultOptionDisabled
                                    />
                                )}
                            </>
                        )}
                        {values.toggleFormType === 'file' && (
                            <>
                                {[LinkTypes.Video.id, LinkTypes.Image.id].includes(values.link_type_id) === true &&
                                    ((values.link_type_id === LinkTypes.Image.id &&
                                        values?.raw_file &&
                                        /(?:\.([^.]+))?$/.exec(values?.raw_file?.name)[1] !== 'svg') ||
                                        values.link_type_id === LinkTypes.Video.id) &&
                                    Object.keys(defaultConfigValues).length > 0 && (
                                        <fieldset
                                            style={{
                                                border: '1px solid #bdc3c7',
                                                borderRadius: '5px',
                                                padding: '10px',
                                                color: '#000000',
                                            }}
                                        >
                                            {!editMode ||
                                                (values?.raw_file && (
                                                    <ConfigHeader link_type_id={values.link_type_id} />
                                                ))}

                                            {values.link_type_id === LinkTypes.Video.id && (
                                                <VideoConfigs
                                                    editMode={editMode}
                                                    defaultConfigValues={defaultConfigValues}
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    configFields={configFields}
                                                    validationTypes={validationTypes}
                                                    readOnlyMode={readOnlyMode}
                                                    setIsVideoCompressionEnable={setIsVideoCompressionEnable}
                                                    getConfigOptionsArray={getConfigOptionsArray}
                                                />
                                            )}
                                            {values.link_type_id === LinkTypes.Image.id && (
                                                <ImageConfigs
                                                    defaultConfigValues={defaultConfigValues}
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    configFields={configFields}
                                                    validationTypes={validationTypes}
                                                    readOnlyMode={readOnlyMode}
                                                    imageSettings={imageSettings}
                                                    setImageSettings={setImageSettings}
                                                    getConfigOptionsArray={getConfigOptionsArray}
                                                />
                                            )}
                                        </fieldset>
                                    )}
                                <Field className={classes.input} name="content_data">
                                    {({ field: { value }, form: { setFieldValue } }) => {
                                        const activeLinkTypeId = +(values.link_type_id !== ''
                                            ? values?.link_type_id
                                            : content?.default_link_type_id);
                                        const dynamicAccept = getAcceptForLinkType(activeLinkTypeId);
                                        const noLinkType = !activeLinkTypeId;

                                        return (
                                        <div className={panelClasses.dropzoneContainer}>
                                            <Dropzone
                                                key={dropzoneKey}
                                                maxSize={getContentMaxSize(activeLinkTypeId)}
                                                accept={dynamicAccept}
                                                maxFiles={1}
                                                multiple={false}
                                                disabled={noLinkType || readOnlyMode}
                                                onDropAccepted={async (files) => {
                                                    if (!readOnlyMode) {
                                                        await onDrop({
                                                            file: files[0],
                                                            setFieldValue: setFieldValue,
                                                            values: values,
                                                        });
                                                    }
                                                }}
                                                onDropRejected={(fileRejections) =>
                                                    onDropError({
                                                        err: fileRejections,
                                                        setFieldError: setFieldError,
                                                        setFieldTouched: setFieldTouched,
                                                        dynamicAccept,
                                                    })
                                                }
                                                validator={(file) =>
                                                    validateVideoCompression(file, values.link_type_id)
                                                }
                                                noClick={editMode ? readOnlyMode : false}
                                            >
                                                {({ getRootProps, getInputProps, isDragActive }) => (
                                                    <div
                                                        {...getRootProps()}
                                                        className={clsx(panelClasses.dropzoneArea)}
                                                        style={{
                                                            backgroundImage: `url(${values.image_url})`,
                                                            backgroundRepeat: 'no-repeat',
                                                            backgroundSize: 'contain',
                                                            backgroundPosition: 'center center',
                                                            outline: isDragActive ? '2px dashed #18BC9B' : undefined,
                                                            opacity: noLinkType ? 0.5 : 1,
                                                        }}
                                                    >
                                                        <input {...getInputProps()} />
                                                        {!values.image_url && (
                                                            <div className={panelClasses.dropzoneText}>
                                                                {noLinkType
                                                                    ? 'Select a Link Type to enable upload'
                                                                    : isDragActive
                                                                    ? 'Drop file here'
                                                                    : 'Upload Content'}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Dropzone>
                                            <div className={panelClasses.previewContainer}>
                                                {values?.img_name && (
                                                    <Chip
                                                        className={panelClasses.previewUploadFileName}
                                                        color="secondary"
                                                        size="small"
                                                        deleteIcon={<MdClose className="react-icon" />}
                                                        onDelete={() => {
                                                            if (!readOnlyMode) {
                                                                setFieldValue('content_data', null);
                                                                setFieldValue('img_name', null);
                                                                setFieldValue('raw_file', null);
                                                                setFieldValue('image_url', '');
                                                                setFieldValue('content_files', []);
                                                                setFieldValue('launch_file', '');
                                                            }
                                                        }}
                                                        label={values?.img_name}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        );
                                    }}
                                </Field>
                                <ErrorMessage className={classes.inputErr} name="content_data" component="div" />
                            </>
                        )}
                    </>
                )}
            {values.link_type_id === LinkTypes.Procedure.id && (
                <>
                    <Field className={classes.input} name="procedure_id">
                        {({ field: { value }, form: { setFieldValue }, meta: { error } }) => (
                            <SelectionAutocomplete
                                title="Procedure"
                                placeholder="Procedure"
                                keyProperty="procedure_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={selectProceduresPicklist}
                                fetchEntityPage={getProceduresPicklist}
                                formField={'procedure_id'}
                                multiSelection={false}
                                entityStatusSelector={selectProceduresPicklistLoadingStatus}
                                readOnly={readOnlyMode}
                                fieldValidation={!!error}
                                onCustomChange={() => {
                                    setFieldValue('procedure_start_node', null);
                                    setFieldValue('procedure_end_node', null);
                                    setProcedureStartEndNode({
                                        procedureStartNode: null,
                                        procedureEndNode: null,
                                    });
                                }}
                            />
                        )}
                    </Field>
                    <ErrorMessage className={classes.inputErr} name="procedure_id" component="div" />
                    <ProcedureStartEndNodeField
                        procedureStartEndNode={procedureStartEndNode}
                        setProcedureStartEndNode={setProcedureStartEndNode}
                    />
                    {!getDefaultContentTypeId(+values?.link_type_id) && (
                        <>
                            <Field className={classes.input} as="select" name="content_type_id" disabled={readOnlyMode}>
                                <option value="" defaultValue disabled>
                                    Select Url Type
                                </option>
                                {getContentTypes(+values?.link_type_id).map((at) => (
                                    <option key={at.label} value={at.id} defaultValue={at.id === 1}>
                                        {at.label}
                                    </option>
                                ))}
                            </Field>
                            <ErrorMessage className={classes.inputErr} name="content_type_id" component="div" />
                        </>
                    )}
                </>
            )}
            {values.link_type_id === LinkTypes.Embed.id && (
                <>
                    <Field
                        margin="dense"
                        as="textarea"
                        variant="outlined"
                        placeholder="Embed Data"
                        className={`${classes.materialTextarea}`}
                        name="embed_data"
                        rows={5}
                        style={{ marginTop: '10px' }}
                        disabled={readOnlyMode}
                    />
                    <Field className={classes.input} as="select" name="content_type_id" disabled={readOnlyMode}>
                        <option value="" defaultValue disabled>
                            Select Url Type
                        </option>
                        {getContentTypes(+values?.link_type_id).map((at) => (
                            <option key={at.label} value={at.id} defaultValue={at.id === 1}>
                                {at.label}
                            </option>
                        ))}
                    </Field>
                    <ErrorMessage className={classes.inputErr} name="content_type_id" component="div" />
                </>
            )}
            {values.link_type_id === LinkTypes.SubjectMatterExpert.id && (
                <>
                    <div className={classes.smeTopInputs}>
                        <div
                            className={clsx({
                                [classes.avatarHidden]: !values?.image_url,
                            })}
                        >
                            <Badge
                                overlap="circular"
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                badgeContent={
                                    <IoCloseCircleSharp
                                        className={classes.removeSmeImage}
                                        onClick={() => {
                                            setFieldValue('content_data', null);
                                            setFieldValue('img_name', null);
                                            setFieldValue('image_url', null);
                                        }}
                                    />
                                }
                            >
                                <Avatar className={clsx(classes.smeAvatar)} alt={'Avatar'} src={values?.image_url} />
                            </Badge>
                        </div>
                        <Field className={classes.input} name="content_data">
                            {({ field: { value }, form: { setFieldValue } }) => (
                                <div
                                    className={clsx(classes.smeAvatar, {
                                        [classes.avatarHidden]: values?.image_url,
                                    })}
                                >
                                    <Dropzone
                                        className={classes.smeDropzone}
                                        maxSize={getContentMaxSize(
                                            +(createMode && values.link_type_id !== ''
                                                ? values?.link_type_id
                                                : content?.default_link_type_id)
                                        )}
                                        accept={getAcceptForLinkType(LinkTypes.Image.id)}
                                        maxFiles={1}
                                        multiple={false}
                                        onDropAccepted={async (files) => {
                                            if (!readOnlyMode) {
                                                await onDrop({
                                                    file: files[0],
                                                    setFieldValue: setFieldValue,
                                                    values: values,
                                                });
                                            }
                                        }}
                                        onDropRejected={(err) =>
                                            onDropError({
                                                err,
                                                setFieldError: setFieldError,
                                                setFieldTouched: setFieldTouched,
                                            })
                                        }
                                        validator={(file) => validateVideoCompression(file, values.link_type_id)}
                                        noClick={editMode ? readOnlyMode : false}
                                    >
                                        {({ getRootProps, getInputProps }) => (
                                            <div {...getRootProps()} className={classes.smeDropzone}>
                                                <input {...getInputProps()} />
                                                {!values.image_url && (
                                                    <div className={classes.smeDropzoneText}>
                                                        <FiUpload
                                                            className={clsx(classes.smeInputIcon, classes.fiFileUpload)}
                                                        />
                                                        <div>Upload</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Dropzone>
                                    <div className={panelClasses.previewContainer}>
                                        {values?.img_name && (
                                            <Chip
                                                className={panelClasses.previewUploadFileName}
                                                color="secondary"
                                                size="small"
                                                deleteIcon={<MdClose className="react-icon" />}
                                                onDelete={() => {
                                                    if (!readOnlyMode) {
                                                        setFieldValue('content_data', null);
                                                        setFieldValue('img_name', null);
                                                        setFieldValue('image_url', '');
                                                    }
                                                }}
                                                label={values?.img_name}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </Field>
                        <div className={classes.fieldWrapper}>
                            <FormTextField
                                label="Name"
                                className={classes.smeMaterialInput}
                                name="sme_name"
                                disabled={readOnlyMode}
                                onChange={(e) => {
                                    setFieldValue('display_name', e.target.value);
                                    setFieldValue('sme_name', e.target.value);
                                }}
                                placeholder={`Name ${values.sme_name === '' ? '*' : ''}`}
                            />
                            <FormTextField
                                label="Title"
                                className={classes.smeMaterialInput}
                                name="sme_title"
                                disabled={readOnlyMode}
                            />
                        </div>
                    </div>
                    <ErrorMessage className={classes.inputErr} name="content_data" component="div" />
                    <Divider />
                    <div className={classes.smeInputContainer}>
                        <HiOutlineMail className={classes.smeInputIcon} />
                        <div className={classes.smeInput}>
                            <label className={classes.smeLabel}>Email</label>
                            <FormTextField
                                placeholder={`Email ${values.sme_email === '' ? '*' : ''}`}
                                className={classes.smeMaterialInput}
                                name="sme_email"
                                disabled={readOnlyMode}
                            />
                        </div>
                    </div>
                    <Divider />
                    <div className={classes.smeInputContainer}>
                        <FiPhone className={classes.smeInputIcon} />
                        <div className={classes.smeInput}>
                            <label className={classes.smeLabel}>Phone</label>
                            <FormTextField
                                placeholder="Phone"
                                className={classes.smeMaterialInput}
                                name="sme_phone"
                                type="tel"
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\s/g, '');
                                    if (/^\d{10}$/.test(val)) {
                                        setFieldValue(
                                            'sme_phone',
                                            `(${val.substring(0, 3)}) ${val.substring(3, 6)}-${val.substring(6)}`
                                        );
                                    } else {
                                        setFieldValue('sme_phone', e.target.value);
                                    }
                                }}
                                disabled={readOnlyMode}
                            />
                        </div>
                    </div>
                    <Divider />
                    <div className={classes.smeInputContainer}>
                        <BiVideo className={classes.smeInputIcon} />
                        <div className={classes.smeInput}>
                            <label className={classes.smeLabel}>Zoom ID</label>
                            <FormTextField
                                placeholder="Zoom ID"
                                className={classes.smeMaterialInput}
                                name="sme_zoom"
                                disabled={readOnlyMode}
                            />
                        </div>
                    </div>
                    <Divider />
                    <div className={classes.smeInputContainer}>
                        <BiVideo className={classes.smeInputIcon} />
                        <div className={classes.smeInput}>
                            <label className={classes.smeLabel}>Teams ID</label>
                            <FormTextField
                                placeholder="Teams ID"
                                className={classes.smeMaterialInput}
                                name="sme_teams"
                                disabled={readOnlyMode}
                            />
                        </div>
                    </div>
                </>
            )}
            <ContentChapterFields onDropError={onDropError} />
            {editMode && values.link_type_id !== LinkTypes.SubjectMatterExpert.id && (
                <>
                    <button type="button" className={classes.preview} onClick={() => handleViewLink(content)}>
                        Preview Versions
                    </button>
                    {readOnlyMode !== true && (
                        <button
                            type="button"
                            className={classes.preview}
                            onClick={() => handleCreateContentClick(values['content_id'])}
                        >
                            Add Version
                        </button>
                    )}
                </>
            )}
        </>
    );
};
